import {useCallback, useEffect, useState} from "react";

export type ResourceState<T> =
  | {status: "loading"}
  | {status: "ready"; data: T}
  | {status: "error"; error: unknown};

export function useResource<T>(load: (signal: AbortSignal) => Promise<T>): ResourceState<T> & {retry: () => void} {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{load: typeof load; attempt: number; state: ResourceState<T>}>(() => ({load, attempt, state: {status: "loading"}}));
  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal).then((data) => {
      if (!controller.signal.aborted) setResult({load, attempt, state: {status: "ready", data}});
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setResult({load, attempt, state: {status: "error", error}});
    });
    return () => controller.abort();
  }, [attempt, load]);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const state = result.load === load && result.attempt === attempt ? result.state : {status: "loading"} as const;
  return {...state, retry};
}
